import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
// ADV-1176 / ADR-0048: /service-agreements is the recurring-agreement register —
// active plans per customer with their DB-computed next visit, next bill, and
// renewal dates. Sibling of the warranty obligations register (ADV-1171).
import { useServiceAgreements } from "@/hooks/use-api";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";
import { StatusBadge } from "@/components/status-badge";
import { formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

function titleCase(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const columns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "display_id",
    header: "Agreement",
    enableSorting: false,
    cell: ({ row }) => {
      const id = row.original.display_id as string | undefined;
      return (
        <span className="typo-mono font-medium transition-colors group-hover:text-[color:var(--color-accent)]">
          {id || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "customer_name",
    header: "Customer",
    enableSorting: false,
    cell: ({ row }) => {
      const name = row.original.customer_name as string | undefined;
      return (
        <span className={name ? "" : "text-[color:var(--color-text-lo)]"}>
          {name || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "plan_name",
    header: "Plan",
    enableSorting: false,
    cell: ({ row }) => {
      const name = row.original.plan_name as string | undefined;
      const frequency = row.original.plan_billing_frequency as
        | string
        | undefined;
      if (!name && !frequency) {
        return <span className="text-[color:var(--color-text-lo)]">—</span>;
      }
      return (
        <span className="flex flex-col">
          <span className="typo-body text-[color:var(--color-text-hi)]">
            {name || "—"}
          </span>
          {frequency && (
            <span className="typo-label text-[color:var(--color-text-lo)]">
              {titleCase(frequency)}
            </span>
          )}
        </span>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.original.status as string | undefined;
      return status ? (
        <StatusBadge status={status} />
      ) : (
        <span className="text-[color:var(--color-text-lo)]">—</span>
      );
    },
  },
  {
    accessorKey: "next_visit_date",
    header: "Next Visit",
    cell: ({ row }) => {
      const value = row.original.next_visit_date as string | undefined;
      return (
        <span className="text-[color:var(--color-text-md)]">
          {value ? formatDate(value) : "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "next_bill_date",
    header: "Next Bill",
    cell: ({ row }) => {
      const value = row.original.next_bill_date as string | undefined;
      return (
        <span className="text-[color:var(--color-text-md)]">
          {value ? formatDate(value) : "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "renewal_date",
    header: "Renews",
    cell: ({ row }) => {
      const value = row.original.renewal_date as string | undefined;
      return (
        <span className="text-[color:var(--color-text-md)]">
          {value ? formatDate(value) : "—"}
        </span>
      );
    },
  },
];

export default function V2ServiceAgreementsPage() {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([
    { id: "next_visit_date", desc: false },
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 50;

  const { data, isLoading } = useServiceAgreements({
    offset: pageIndex * pageSize,
    limit: pageSize,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
  });

  const agreements = (data?.service_agreements ?? []) as Row[];
  const total = data?.total ?? agreements.length;

  return (
    <ListPage
      title="Service Agreements"
      subtitle={`${total.toLocaleString()} recurring agreements on record`}
    >
      <DataTable
        columns={columns}
        data={agreements}
        total={total}
        isLoading={isLoading}
        onRowClick={(row) => navigate(`/service-agreements/${row.id}`)}
        emptyMessage="No service agreements yet"
        emptyDescription="Agreements appear here when a recurring service plan is sold on a won opportunity."
        pageSize={pageSize}
        pageIndex={pageIndex}
        onPaginationChange={setPageIndex}
        sorting={sorting}
        onSortingChange={setSorting}
      />
    </ListPage>
  );
}
